/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React, { useState, useRef } from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/grid.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/rail.css'
import './styles.scss'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { AutofitIFrame } from '@ta-interaktiv/react-autofit-iframe'
import TEXTS from './texts.yml'
import { CSSTransition } from 'react-transition-group'
import { Canvas } from 'components/canvas'
import { Panel } from 'components/panel'

// TODO Set the correct article ID here
const ARTICLE_ID = '157775190319'

export function AppComponent() {
  const [panelIsOpen, setPanelIsOpen] = useState(false)
  const [selectedNurse, setSelectedNurse] = useState(false)
  const header = useRef(null)

  return (
    <div className='index'>
      <Masthead inverted articleId={ARTICLE_ID} />
      <ThemeSwitch />

      <header className='ui vertical  very fitted  segment header-canvas-container'>
        <div className='ui container'>
          <div className='ui header' ref={header}>
            <h1>Les infirmières, héroïnes dévouées de l’année 2020</h1>
            <h2>
              Ces derniers mois, elles ont été en première ligne pour lutter
              contre le coronavirus. Gros plan sur une profession aux facettes
              multiples, mais dans laquelle l’humain est toujours au centre.
            </h2>
            <div className='ta metainfo'>
              <div className='ta author'>
                Sébastien Jubin, Lucie Monnat, Caroline Zuercher et
                Jean-Philippe Bernard
              </div>
              <div className='ta updated'>Mis à jour le 27.12.2020</div>
            </div>
          </div>
        </div>
      </header>

      <Canvas
        data={TEXTS.nurses}
        handleClick={p => {
          setPanelIsOpen(true)
          setSelectedNurse(p)
          document.body.classList.add('disable_scroll')
        }}
      />

      <div className='ui vertical segment main-story'>
        <div
          className='ui ta text container'
          style={{ position: 'relative', marginTop: '4rem' }}
        >
          <p>
            «Les infirmières sont compétentes pour évaluer les besoins de santé,
            planifier et coordonner les soins, assurer la communication à
            l’interface d’une multitude d’acteurs différents…» Très académique,
            la définition du poste d’infirmière trouvée sur le site du CHUV ne
            dit rien du stress, de la charge mentale, des cernes, des journées
            interminables. Rien de sa beauté non plus, de son rôle fondamental,
            ni de sa complexité. Sans infirmière, un hôpital ne tourne pas.
          </p>
          <h4>Faire, défaire, refaire</h4>
          <p>
            Alors que le monde entier a été englouti sous la vague Covid,
            emportant dans ses rouleaux nos vies et nos économies, le métier
            s’est dressé comme l’un des premiers remparts contre le virus. Avant
            la pandémie, l’OMS avait désigné 2020 comme l’Année de l’infirmière.
            Les professionnels ne s’attendaient certainement pas à fêter cet
            honneur de façon si brutale, avec un virus qui s’acharne à tester
            quotidiennement leurs limites. Les infirmières ont passé toute
            l’année au front et y sont encore, à l’affût d’une troisième vague
            qui menace alors que la deuxième n’est pas terminée.
          </p>

          <p>
            «Cela dépendra de la manière dont se comporte la population, surtout
            pendant les Fêtes, analyse Isabelle Lehn, directrice des soins du
            CHUV. Nous ne pouvons qu’être en réaction.» Faire, défaire, refaire…
            tout cela dans une ambiance incertaine, sans savoir de quoi sera
            fait demain. «Nous avions prévu l’hypothèse d’une deuxième vague,
            mais nous espérions qu’elle ne se présenterait pas, soupire son
            homologue des HUG, Sandra Merkli. Comme tout le monde, j’aimerais
            passer à un autre chapitre, me projeter dans d’autres sujets, tant
            au niveau professionnel que personnel.»{' '}
          </p>

          <aside
            className='ui vertical very fitted segment widescreen hidden large screen hidden small'
            style={{ marginBottom: '1em' }}
            onClick={() => {
              setSelectedNurse({
                name: 'Politique',
                info: '',
                text: TEXTS.panels.politic
              })
              setPanelIsOpen(true)
            }}
          >
            <div className='story-hint'>
              <div className='icon-container'>
                <i className='file alternate icon' />
              </div>
              <div className='hint-container'>
                <div>Politique</div>
                <div>
                  <span className='pseudo-link'>
                    Les infirmières se battent aussi sur le terrain politique
                  </span>
                </div>
              </div>
            </div>
          </aside>
          <aside
            className='ui right rail computer or lower hidden'
            style={{ top: '50%' }}
            onClick={() => {
              setSelectedNurse({
                name: 'Politique',
                info: '',
                text: TEXTS.panels.politic
              })
              setPanelIsOpen(true)
            }}
          >
            <div className='story-hint'>
              <div className='icon-container'>
                <i className='file alternate icon' />
              </div>
              <div className='hint-container'>
                <div>Politique</div>
                <div>
                  <span className='pseudo-link'>
                    Les infirmières se battent aussi sur le terrain politique
                  </span>
                </div>
              </div>
            </div>
          </aside>

          <p>
            On ne dit pas «infirmière en chef», mais «directrice des soins». On
            pourrait aussi les qualifier de cheffes d’orchestre, de pièces
            maîtresses dans le grand échiquier que forment les services d’un
            hôpital. Sandra Merkli et Isabelle Lehn tiennent un grand rôle de
            coordination et d’organisation. La première dirige depuis trois ans
            les 4500 infirmiers des HUG, tandis qu’Isabelle Lehn, en place
            depuis 2014, coordonne les activités des 3200 infirmiers du CHUV.
            «L’infirmière, c’est le dernier filet de sécurité de l’hôpital, que
            ce soit pour le patient ou au niveau de l’organisation, de la prise
            en charge tout au long de la journée, du mois et de la semaine,
            explique cette dernière. Nous avons des liens avec tout le monde.
            Nous exposons les problèmes et présentons des pistes de solutions.»
          </p>
          <h4>Première vague</h4>
          <p>
            En février, Isabelle Lehn observe l’onde s’approcher lentement
            depuis la Chine, puis l’Italie. La crise devient très concrète le
            5 mars, jour où le CHUV enregistre le premier décès en Suisse lié au
            Covid-19. Mais le maître-mot d’Isabelle Lehn est «anticipation».
            «Nous étions prêts à agir. Nous avons toujours été proactifs. La
            surprise n’est pas un mot qui fait partie de mon vocabulaire, ni une
            émotion que j’ai ressentie.» Aux HUG, le premier patient est
            hospitalisé le 27 février. Réunions de crise entre direction,
            services et autorités cantonales deviennent le lot quotidien des
            deux hôpitaux. La situation est réévaluée au fil des heures, des
            arrivées de patients et des directives de l’Office fédéral de la
            santé publique (OFSP).
          </p>
        </div>

        <div className='ui ta container'>
          <div className='image-container'>
            <img src={require('../../images/isabelle_lehn_inline.jpg')} />
            <small>
              Isabelle Lehn, directrice des soins du CHUV. Photo Yvain Genevay /
              Le Matin Dimanche
            </small>
          </div>
        </div>
        <div className='ui ta text container' style={{ position: 'relative' }}>
          <p>
            Le virus balaie tous les autres projets. Les deux directrices de
            soins s’attellent à mettre en place de nouvelles structures,
            embaucher du personnel, réaffecter des professionnels, les former
            dans l’urgence, repenser les interactions, informer les équipes, les
            encourager, les rassurer par vidéos interposées. «J’ai appris à
            travailler différemment, se souvient Sandra Merkli. Comme les
            autres.»
          </p>
          <h4>Changements de fonction</h4>
          <p>
            En une dizaine de jours, les hôpitaux se réorganisent complètement.
            Le grand chamboulement touche une grande majorité des infirmiers.
            «Même ceux qui n’ont pas été au contact des patients Covid se sont
            retrouvés dans une situation délicate, explique Sandra Merkli.
            Certains de leurs collègues sont partis dans d’autres services. Des
            intérimaires sont arrivés et il a fallu les encadrer. On a demandé
            aux collaborateurs de jongler.»
          </p>
          <p>
            Des employés ont ainsi changé de fonction du jour au lendemain. «Une
            infirmière a, par exemple, retrouvé les soins intensifs après douze
            ans dans l’administration, raconte Sandra Merkli. Ces personnes ont
            dû quitter le travail qu’elles avaient choisi pour en faire un
            autre. Elles se sont retrouvées dans une nouvelle équipe et ont
            effectué des gestes auxquels elles n’étaient pas habituées. Tout
            cela a créé un stress énorme.»
          </p>
          <p>
            Même exercice au CHUV, où toutes les vacances, congés non payés et
            formations de l’ensemble de l’hôpital sont annulés dès mars. «Il a
            fallu d’abord déconstruire pour ensuite réorienter les ressources et
            transformer l’hôpital», souligne Isabelle Lehn.
          </p>
          <h4>Sens du métier</h4>
          <p>
            Malgré tout, les troupes sont au rendez-vous. Personne ne fuit, au
            contraire. «La solidarité constitue la colonne vertébrale des
            infirmières, des médecins, des gens qui travaillent dans un hôpital,
            souligne avec fierté Isabelle Lehn. Même des personnes considérées
            comme vulnérables nous demandaient si elles pouvaient se rendre
            utiles.»
          </p>

          <div className='ta-quote'>
            <q>
              Ces derniers mois ont permis au public de porter un regard
              beaucoup plus aiguisé sur la richesse de nos compétences.
            </q>
            <div>
              <small>Isabelle Lehn, directrice des soins du CHUV</small>
            </div>
          </div>

          <p>
            Aux yeux de Sandra Merkli, la crise aura eu un aspect positif, celui
            de mettre en lumière son «magnifique métier». «Les infirmiers ont
            montré de quoi ils étaient capables. Notre profession avait déjà
            connu des avancées majeures. Aux HUG, par exemple, des infirmiers
            sont adjoints du chef dans certains services.» Elle ne pense pas
            qu’on reviendra en arrière. «Ces derniers mois ont permis au public
            de porter un regard beaucoup plus aiguisé sur la richesse de nos
            compétences. Une infirmière n’est pas uniquement quelqu’un qui fait
            une piqûre ou un pansement», abonde Isabelle Lehn.
          </p>

          <h4>Profonde mutation</h4>
          <p>
            La profession a effectivement beaucoup évolué au cours de ces
            dernières années. «Il y a trente ans, nous étions des exécutantes.
            Aujourd’hui, nous gagnons de plus en plus en autonomie. Par exemple,
            des collègues font des consultations autonomes, sans médecin à leur
            côté.» Les connaissances scientifiques ont également augmenté,
            tandis que la prise en compte de la psychologie humaine s’est
            développée. «Au début de ma carrière, nous pensions savoir ce qui
            était bien pour le patient, explique Sandra Merkli. Aujourd’hui,
            nous devons mieux l’informer et travailler avec lui. Il faut
            expliquer, guider, informer les proches. Ces changements sont
            passionnants!»
          </p>
          <p>
            Isabelle Lehn est tout aussi éprise de ce métier, à la fois complexe
            et diversifié. «Infirmière, c’est un rôle. Nous sommes là pour
            évaluer les risques du patient et faire en sorte de lui éviter des
            complications», explique cette Franco-Suisse. Elle se définit
            également comme une sorte d’interprète. «L’infirmière est là pour
            voir où le patient en est dans son projet de vie. On ne va pas
            soigner de la même façon une personne qui dit: «Moi, je ne veux rien
            changer à ma vie. Je vais continuer à fumer, à boire. Je vais mourir
            dans trois mois et ça me va.» Il faut respecter ce plan-là, comme il
            faut tout faire pour la personne qui dit: «J’ai des enfants, je veux
            vivre.»
          </p>
          <h4>Charge mentale</h4>
          <p>
            Isabelle Lehn évoque une charge mentale importante, peut-être
            l’aspect le plus difficile du métier. Elle pèse sur leurs épaules
            lorsqu’il faut gérer les émotions des patients, affectés par la
            fatigue, la peur ou un espace cognitif altéré. La charge mentale est
            encore là quand on ne fait que «parler, manger, dormir Covid depuis
            plus de six mois», pour reprendre les termes de Sandra Merkli.
          </p>

          <aside
            className='ui vertical very fitted segment widescreen hidden large screen hidden small'
            style={{ marginBottom: '1em' }}
            onClick={() => {
              setSelectedNurse({
                name: 'Cinéma',
                info: '',
                text: TEXTS.panels.cinema
              })
              setPanelIsOpen(true)
            }}
          >
            <div className='story-hint'>
              <div className='icon-container'>
                <i className='file alternate icon' />
              </div>
              <div className='hint-container'>
                <div>Sur écran, elles sont des anges</div>
                <div>
                  <span className='pseudo-link'>
                    Les infirmières sont d’incontournables héroïnes de cinéma. 
                  </span>
                </div>
              </div>
            </div>
          </aside>
          <aside
            className='ui right rail computer or lower hidden'
            style={{ top: '75%' }}
            onClick={() => {
              setSelectedNurse({
                name: 'Cinéma',
                info: '',
                text: TEXTS.panels.cinema
              })
              setPanelIsOpen(true)
            }}
          >
            <div className='story-hint'>
              <div className='icon-container'>
                <i className='file alternate icon' />
              </div>
              <div className='hint-container'>
                <div>Sur écran, elles sont des anges</div>
                <div>
                  <span className='pseudo-link'>
                    Les infirmières sont d’incontournables héroïnes de cinéma. 
                  </span>
                </div>
              </div>
            </div>
          </aside>

          <p>
            L’incertitude n’aide pas. «Souvent, pendant ma carrière, je me suis
            posé certaines questions: «Comment faire en cas d’attaque
            terroriste, comme le World Trade Center aux États-Unis? En cas de
            gros accident?» souligne Isabelle Lehn. On a même des exercices de
            simulation, par exemple, si un bateau de la CGN coule. On sait gérer
            un afflux massif de patients sur quelques heures. Mais agir sur
            plusieurs semaines, mois, sans savoir jusqu’à quand, ça, on ne
            connaissait pas.»
          </p>
          <p>
            Et puis, il y a l’imprévisibilité de ce satané virus, ses symptômes
            étranges et aléatoires. «On savait comment se transmettait la
            maladie, mais on ne savait pas quelles conséquences elle aurait chez
            les patients, poursuit Isabelle Lehn. Or, le CHUV ne ferme jamais
            ses portes. Mon rôle aura donc été en permanence de m’assurer qu’il
            y a suffisamment de lits pour accueillir les prochains patients, de
            professionnels pour les soigner et de matériel pour protéger tout le
            monde. À cette condition, je peux rentrer chez moi le soir, et
            dormir.»
          </p>
        </div>

        <div className='ui ta container'>
          <div className='image-container'>
            <img src={require('../../images/sandra_merkli_inline.jpg')} />
            <small>
              Sandra Merkli, directrice des soins des HUG. Photo Yvain Genevay /
              Le Matin Dimanche
            </small>
          </div>
        </div>

        <div className='ui ta text container' style={{ position: 'relative' }}>
          <h4>Reconnaissance</h4>
          <p>
            Ces derniers mois, Sandra Merkli a parfois connu «des baisses de
            régime liées à la fatigue physique et à l’omniprésence du Covid». La
            reconnaissance lui a permis de tenir le coup. «Il me suffit de
            croiser quelqu’un qui me raconte une belle histoire, de lire un
            témoignage. Cela me fait du bien.» Les applaudissements lui ont
            aussi fait plaisir, sans pour autant ressentir de la déception
            lorsque les fenêtres sont restées closes lors de la deuxième vague.
            «Je n’y ai pas vu un désaveu. Comme nous, le public ne voulait pas
            revivre une période identique.»
          </p>
          <h4>Deuxième vague</h4>
          <p>
            Les mois passent et la fatigue devient une compagne de route. Tant
            Isabelle Lehn que Sandra Merkli décrochent quelques semaines en été.
            Comme pour tout le monde, les vacances ont une saveur différente,
            quelque peu altérée. Puis vient l’automne, et la deuxième vague
            déferle, plus surprenante que la première. «En septembre et jusqu’au
            tout début octobre, on me demandait quand serait le retour à la
            normale, se rappelle Isabelle Lehn. Je répondais qu’il fallait
            oublier le normal d’hier, que la normalité d’aujourd’hui était cette
            incertitude que nous vivons, du moins jusqu’au vaccin.»
          </p>
          <p>
            Lorsque le rouleau arrive, les séances de direction reprennent. En
            une semaine, tout le système, désormais connu, est remis en place.
            Cette fois, la difficulté réside davantage dans la gestion d’un
            personnel qui n’a pas eu le temps de récupérer de la première vague.
            À Genève, début décembre, des soignants en anesthésie des HUG ont
            fait part de leur épuisement dans la presse, évoquant des semaines
            de travail de soixante heures durant la première vague et
            l’annulation de congés annuels. «Nous avons essayé de respecter le
            plus possible le nombre d’heures des employés, répond Sandra Merkli.
            Mais tout le monde a fait preuve de souplesse, avec des horaires
            très variables, en fonction des flux de patients.» Sans oublier que,
            durant la deuxième vague, de nombreux employés sont tombés malades
            et ont dû être remplacés.
          </p>
          <aside
            className='ui vertical very fitted segment widescreen hidden large screen hidden small'
            style={{ marginBottom: '1em' }}
            onClick={() => {
              setSelectedNurse({
                name: 'En chiffres',
                info: '',
                text: ''
              })
              setPanelIsOpen(true)
            }}
          >
            <div className='story-hint'>
              <div className='icon-container'>
                <i className='file alternate icon' />
              </div>
              <div className='hint-container'>
                <div>La vie d’infirmière en chiffres</div>
                <div>
                  <span className='pseudo-link'>
                    Des salaires au taux de sortie du métier, les données sont
                    variées. Tour d'horizon.
                  </span>
                </div>
              </div>
            </div>
          </aside>
          <aside
            className='ui right rail computer or lower hidden'
            style={{ top: '40%' }}
            onClick={() => {
              setSelectedNurse({
                name: 'En chiffres',
                info: '',
                text: ''
              })
              setPanelIsOpen(true)
            }}
          >
            <div className='story-hint'>
              <div className='icon-container'>
                <i className='file alternate icon' />
              </div>
              <div className='hint-container'>
                <div>La vie d’infirmière en chiffres</div>
                <div>
                  <span className='pseudo-link'>
                    Des salaires au taux de sortie du métier, les données sont
                    variées. Tour d'horizon.
                  </span>
                </div>
              </div>
            </div>
          </aside>
          <p>
            À Lausanne aussi, il a fallu gérer le taux d’absentéisme plus
            important, notamment en raison d’un nombre élevé d’isolements et de
            quarantaines. Cette fois cependant, pas question de suspendre les
            vacances. Isabelle Lehn a fait en sorte que les heures de travail
            n’explosent pas. Le CHUV n’a d’ailleurs recouru à la possibilité
            d’augmenter le seuil d’heures hebdomadaires à 60 ni pendant la
            première, ni pendant la seconde vague. Au CHUV comme aux HUG, on a
            comblé les trous avec des étudiants en fin de formation, un
            prolongement des CDD, beaucoup de missions intérimaires, ou encore
            l’aide de soignants retraités.
          </p>
          <h4>Gérer la mort</h4>
          <p>
            À côté de tout cela, il faut gérer la mort, qui s’invite bien
            souvent au chevet des malades des soins intensifs. Un week-end de
            novembre aux CHUV, cinq personnes sont décédées dans un laps de
            temps très court. Même épreuve aux HUG. «Un collègue m’a dit qu’en
            vingt-quatre heures, il avait vu quatre personnes décéder. C’est
            difficile», raconte Sandra Merkli. Surtout quand ce n’est pas
            habituellement votre quotidien. «Des infirmières anesthésistes ont,
            par exemple, travaillé aux soins intensifs car elles savent gérer
            les patients intubés. Mais en chirurgie, elles sont rarement
            confrontées au décès, ou aux situations de retrait thérapeutique où
            l’on ne donne plus que des soins de confort.»
          </p>

          <div className='ta-quote'>
            <q>
              Il me suffit de croiser quelqu’un qui me raconte une belle
              histoire, de lire un témoignage. Cela me fait du bien.
            </q>
            <div>
              <small>Sandra Merkli, directrice des soins aux HUG</small>
            </div>
          </div>

          <p>
            «La mort fait partie du métier, complète Isabelle Lehn. On sait
            pourquoi elle est là, quel est son sens. Un décès est un moment
            riche en émotion, surtout au contact des proches. Il faut être très
            professionnel pour que les gens puissent mettre fin au lien avec le
            patient – ou plutôt terminer un lien vivant, car ce lien continue
            après. Il faut pouvoir être là pour leur permettre de vivre le mieux
            possible ce passage.» Un exercice qui requiert une «forte énergie».
            «Quand vous en avez plusieurs dans la même journée ou dans le même
            horaire, vous n’en sortez pas totalement indemne.»
          </p>
          <p>
            Au CHUV comme aux HUG, le personnel soignant bénéficie d’une aide
            psychologique. Il peut aussi trouver du réconfort dans la guérison
            des patients. De nombreuses hospitalisations connaissent un
            dénouement heureux. Au 21 décembre 2020 à 6h, 922 patients Covid
            étaient, par exemple, sortis du CHUV depuis le début de l’épidémie.
          </p>
        </div>
      </div>

      <Footer
        mailTo='elisa.blondel@tamedia.ch'
        hidePortfolioTeaser
        locale='fr'
        articleId={ARTICLE_ID}
        credits={[
          [
            'Textes',
            'Sébastien Jubin, Lucie Monnat, Caroline Zuercher et Jean-Philippe Bernard'
          ],
          ['Infographies', 'Océane Haenni'],
          ['Coordination', 'Elisa Blondel'],
          ['Réalisation', 'Mathieu Rudaz']
        ]}
      />

      <CSSTransition
        unmountOnExit
        in={panelIsOpen}
        timeout={200}
        classNames='portrait-view-container'
      >
        <div className='portrait-view-container'>
          <div
            className='dimmer'
            onClick={() => {
              setPanelIsOpen(false)
              setSelectedNurse(false)
              document.body.classList.remove('disable_scroll')
            }}
          />
          <div
            className='portrait-view'
            onClick={() => {
              setPanelIsOpen(false)
              setSelectedNurse(false)
              document.body.classList.remove('disable_scroll')
            }}
          >
            <Panel text={selectedNurse} free />
          </div>
        </div>
      </CSSTransition>
    </div>
  )
}
